import React, { useState, useMemo } from 'react'
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import 'react-tabs/style/react-tabs.css';
import {
    IconCaretDownFilled,
    IconSettings,
    IconChevronLeft,
    IconHome,
    IconLayoutGrid,
    IconFolder,
    IconWorld,
    IconDiamond,
    IconPuzzle,
    IconLock,
    IconFiles,
    IconPencil,
    IconTag,
    IconChartBar,
    IconMap,
    IconMailOpened,
    IconPower,
    IconChecklist,
    IconTimelineEventPlus,
    IconCalendar,
    // IconMail,
    // IconMessages,
    IconUsersGroup,
    IconBriefcase,
    IconCreditCard,
    IconChartHistogram,
    IconTicket,
    IconPlaneDeparture,
    IconCar,
} from '@tabler/icons-react'
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../common/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { IconUser } from '@tabler/icons-react';
import { IconClipboardText } from '@tabler/icons-react';
import { IconListCheck } from '@tabler/icons-react';
import { IconList } from '@tabler/icons-react';
import { IconClock } from '@tabler/icons-react';
import { IconMessageCircle } from '@tabler/icons-react';
import { IconFileText } from '@tabler/icons-react';
import { usePendingApprovals } from '../../hooks/usePendingApprovals';
import { usePayrollRunsAssignedToMe } from '../../hooks/usePayroll';
import { useVehicleRequestSummary } from '../../hooks/useVehicleRequests';
import { isActionableForMe } from '../../pages/payroll/utils';

export default function Sidebar() {
    const location = useLocation()
    const navigate = useNavigate()
    const urlPath = location.pathname
    const { user, hasAnyPermission, logout } = useAuth();

    // Payroll runs assigned to me as reviewer/approver — identity-based, not permission-
    // or org-scoped, so a cross-org assignee with no payroll permission still sees their queue.
    const { data: myPayrollRuns } = usePayrollRunsAssignedToMe({ enabled: !!user && !user.is_superuser });
    const myActionablePayrollRuns = useMemo(
        () => (myPayrollRuns || []).filter(r => isActionableForMe(r, user)),
        [myPayrollRuns, user]
    );
    const hasAssignedPayrollRuns = myActionablePayrollRuns.length > 0;

    const handleLogout = () => {
        logout();
        navigate('/auth-login');
    };

    const [menuItem, setMenuItem] = useState(null);
    const menuItemButton = (key) => {
        setMenuItem(menuItem === key ? null : key);
    };

    const [subMenuItem, setSubMenuItem] = useState(null);
    const subMenuItemButton = (key) => {
        setSubMenuItem(subMenuItem === key ? null : key);
    };

    const { selectedOption, handleOptionClick, toggle_darkmode, darkmode, setActive, toggle_miniSidebar, miniSidebar, isHovered } = useTheme();

    const menuItemLink = [
        // ==================== ADMIN & HR MENU ====================
        {
            icon: IconHome,
            url: "Dashboard",
            link: "/dashboard",
            hrMenu: true,
            // Dashboard visible to all authenticated users
        },
        {
            icon: IconUsersGroup,
            url: "Employees",
            hrMenu: true,
            // Requires any HR permission to view
            permissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
            children: [
                {
                    url: "All Employees",
                    link: "/employee",
                },
                {
                    url: "Departments",
                    link: "/department",
                },
            ],
        },
        // Documents
        {
            icon: IconFileText,
            url: "Documents",
            hrMenu: true,
            permissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
            children: [
                {
                    url: "All Documents",
                    link: "/documents",
                },
                {
                    url: "Archived Documents",
                    link: "/documents/archived",
                },
                {
                    url: "Purge Queue",
                    link: "/documents/purge-queue",
                },
            ],
        },
        // MODULE 1: Recruitment & ATS (hidden)
        // {
        //     icon: IconBriefcase,
        //     url: "Recruitment",
        //     hrMenu: true,
        //     permissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
        //     children: [
        //         {
        //             url: "Job Descriptions",
        //             link: "/recruitment/jobs",
        //         },
        //         {
        //             url: "Applications",
        //             link: "/recruitment/applications",
        //         },
        //         {
        //             url: "Interview Schedule",
        //             link: "/recruitment/interviews",
        //         },
        //         {
        //             url: "Offer Letters",
        //             link: "/recruitment/offers",
        //         },
        //     ],
        // },
        // MODULE 2: Leave Management
        {
            icon: IconCalendar,
            url: "Leave Management",
            hrMenu: true,
            permissions: ['rbacapp.lve_view', 'rbacapp.lve_act', 'rbacapp.lve_approve'],
            children: [
                {
                    url: "Leave Requests",
                    link: "/leave",
                },
                {
                    url: "My Leave Balance",
                    link: "/my-leave-balance",
                },
                {
                    url: "Employee Leave Balance",
                    link: "/leave-balance",
                    permissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
                },
                // {
                //     url: "Leave Reports",
                //     link: "/leave-reports",
                // },
                {
                    url: "Leave Types",
                    link: "/leave-types",
                },
                {
                    url: "Holidays",
                    link: "/holiday",
                },
            ],
        },
        // MODULE 3: Performance Management
        {
            icon: IconChartBar,
            url: "Performance",
            hrMenu: true,
            permissions: ['rbacapp.pdp_view', 'rbacapp.pdp_act', 'rbacapp.pdp_approve'],
            children: [
                {
                    url: "KPI Dashboard",
                    link: "/performance/kpi",
                },
                {
                    url: "360 Feedback",
                    link: "/performance/360-feedback",
                },
                {
                    url: "Performance History",
                    link: "/performance/reports",
                },
            ],
        },
        // MODULE 4: Probation Assessment (hidden)
        // {
        //     icon: IconClipboardText,
        //     url: "Probation",
        //     hrMenu: true,
        //     permissions: ['rbacapp.pdp_view', 'rbacapp.pdp_act', 'rbacapp.pdp_approve'],
        //     children: [
        //         {
        //             url: "Goal Setting",
        //             link: "/probation/goals",
        //         },
        //         {
        //             url: "Assessments",
        //             link: "/probation/assessments",
        //         },
        //         {
        //             url: "Probation Tracking",
        //             link: "/probation/tracking",
        //         },
        //     ],
        // },
        // MODULE 5: Payroll
        {
            icon: IconCreditCard,
            url: "Payroll",
            hrMenu: true,
            permissions: ['rbacapp.pay_upload', 'rbacapp.pay_review', 'rbacapp.pay_approve', 'rbacapp.pay_publish'],
            children: [
                {
                    url: "Payroll Upload",
                    link: "/payroll/template",
                    permissions: ['rbacapp.pay_upload'],
                },
                {
                    url: "Payroll Runs",
                    link: "/payroll/runs",
                    excludePermissions: ['rbacapp.pay_approve'],
                },
                {
                    url: "My Payslips",
                    link: "/payslip",
                },
                {
                    url: "My Signature",
                    link: "/payroll/signature",
                    permissions: ['rbacapp.pay_review', 'rbacapp.pay_approve'],
                },
            ],
        },
        // Project Management
        {
            icon: IconFolder,
            url: "Project Management",
            hrMenu: true,
            permissions: ['rbacapp.gp_view_draft', 'rbacapp.gp_view_review', 'rbacapp.gp_view_submitted', 'rbacapp.gp_view_awarded', 'rbacapp.gp_view_rejected', 'rbacapp.gp_draft', 'rbacapp.gp_review', 'rbacapp.gp_submit', 'rbacapp.gp_award', 'rbacapp.gp_reject'],
            children: [
                {
                    url: "Grant & Proposal",
                    link: "/project-management/grant-proposal",
                },
            ],
        },
        // MODULE 6: Travel Advance
        {
            icon: IconPlaneDeparture,
            url: "Travel",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.trv_self', 'rbacapp.trv_security', 'rbacapp.trv_view_all', 'rbacapp.trv_finance', 'rbacapp.trv_partner'],
            link: "/travel",
        },
        // MODULE 7: Vehicle Request
        {
            icon: IconCar,
            url: "Vehicle Request",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.veh_view', 'rbacapp.veh_act', 'rbacapp.veh_view_all', 'rbacapp.veh_dispatch'],
            link: "/vehicle-requests",
        },
        // MODULE 8: IT Support Tickets
        {
            icon: IconTicket,
            url: "IT Support",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.its_view', 'rbacapp.its_act', 'rbacapp.its_triage', 'rbacapp.its_agent'],
            link: "/support/tickets",
        },
        // Supporting Features
        {
            icon: IconBriefcase,
            url: "Accounts",
            hrMenu: true,
            permissions: ['rbacapp.sys_admin'],
            children: [
                {
                    url: "Payments",
                    link: "/payment",
                },
                {
                    url: "Expenses",
                    link: "/expense",
                },
                {
                    url: "Invoices",
                    link: "/invoice",
                },
            ],
        },
        // Reports (hidden)
        // {
        //     icon: IconChartHistogram,
        //     url: "Reports",
        //     hrMenu: true,
        //     permissions: ['rbacapp.rep_view', 'rbacapp.rep_self'],
        //     children: [
        //         {
        //             url: "Expense Report",
        //             link: "/expense-report",
        //         },
        //         {
        //             url: "Invoice Report",
        //             link: "/invoice-report",
        //         },
        //     ],
        // },
        {
            icon: IconClock,
            url: "Timesheet",
            link: "/timesheet",
            hrMenu: true,
            permissions: ['rbacapp.ts_view', 'rbacapp.ts_act', 'rbacapp.ts_approve', 'rbacapp.ts_self'],
        },
        {
            icon: IconUser,
            url: "Users",
            link: "/user",
            hrMenu: true,
            permissions: ['rbacapp.rbac_admin', 'rbacapp.rbac_userrole_manage'],
            excludePermissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
        },
        {
            icon: IconSettings,
            url: "System Setup",
            link: "/system-setup",
            hrMenu: true,
            permissions: ['rbacapp.sys_admin'],
        },

        // ==================== SELF-SERVICE MENU (for users with only _self permissions) ====================
        {
            icon: IconCalendar,
            url: "My Leave",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.lve_self'],
            excludePermissions: ['rbacapp.lve_view', 'rbacapp.lve_act', 'rbacapp.lve_approve'],
            children: [
                {
                    url: "Leave Balance",
                    link: "/my-leave-balance",
                },
                {
                    url: "Apply for Leave",
                    link: "/leave",
                },
                {
                    url: "Leave History",
                    link: "/leave-history",
                },
            ],
        },
        {
            icon: IconChartBar,
            url: "My Performance",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.pdp_self'],
            excludePermissions: ['rbacapp.pdp_view', 'rbacapp.pdp_act', 'rbacapp.pdp_approve'],
            children: [
                {
                    url: "KPI Dashboard",
                    link: "/performance/kpi",
                },
                {
                    url: "360 Feedback",
                    link: "/performance/360-feedback",
                },
                {
                    url: "Performance History",
                    link: "/performance/reports",
                },
            ],
        },
        {
            icon: IconCreditCard,
            url: "My Payroll",
            hrMenu: true,
            selfService: true,
            excludePermissions: ['rbacapp.pay_upload', 'rbacapp.pay_review', 'rbacapp.pay_approve', 'rbacapp.pay_publish'],
            children: [
                {
                    url: "My Payslips",
                    link: "/payslip",
                },
            ],
        },
        {
            icon: IconClipboardText,
            url: "My Timesheet",
            link: "/timesheet",
            hrMenu: true,
            selfService: true,
            permissions: ['rbacapp.ts_self'],
            excludePermissions: ['rbacapp.ts_view', 'rbacapp.ts_act', 'rbacapp.ts_approve'],
        },
        {
            icon: IconFileText,
            url: "My Documents",
            link: "/documents",
            hrMenu: true,
            selfService: true,
            excludePermissions: ['rbacapp.hr_view', 'rbacapp.hr_act', 'rbacapp.hr_approve'],
        },

        // ==================== APPROVALS MENU (for line managers) ====================
        {
            icon: IconCalendar,
            url: "Leave Requests",
            link: "/approvals/leave",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.lve_approve', 'rbacapp.hr_approve', 'rbacapp.hr_act', 'rbacapp.hr_view'],
        },
        {
            icon: IconClock,
            url: "Timesheet Approvals",
            link: "/approvals/timesheet",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.ts_approve', 'rbacapp.hr_approve', 'rbacapp.hr_act', 'rbacapp.hr_view'],
        },
        // {
        //     icon: IconClock,
        //     url: "HR Timesheet Approvals",
        //     link: "/approvals/timesheet/hr",
        //     hrMenu: true,
        //     permissions: ['rbacapp.hr_act', 'rbacapp.hr_approve'],
        // },
        {
            icon: IconChartBar,
            url: "KPI Approvals",
            link: "/approvals/kpi",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.pdp_approve'],
        },
        {
            icon: IconMessageCircle,
            url: "360 Feedback",
            link: "/performance/360-feedback/hr",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.pdp_approve'],
        },
        {
            icon: IconPlaneDeparture,
            url: "Travel Approvals",
            link: "/approvals/travel",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.trv_security', 'rbacapp.trv_finance', 'rbacapp.trv_partner', 'rbacapp.trv_view_all'],
        },
        {
            icon: IconCar,
            url: "Vehicle Approvals",
            link: "/approvals/vehicle",
            projectMenu: true,
            hideFromSuperuser: true,
            // Reviewing is identity-based, but the queue still needs veh_view to load.
            permissions: ['rbacapp.veh_view', 'rbacapp.veh_view_all', 'rbacapp.veh_dispatch'],
        },
        {
            icon: IconTicket,
            url: "Support Queue",
            link: "/support/queue",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.its_triage', 'rbacapp.its_agent'],
        },
        {
            icon: IconCreditCard,
            url: "Payroll Approvals",
            link: "/approvals/payroll",
            projectMenu: true,
            hideFromSuperuser: true,
            permissions: ['rbacapp.pay_approve'],
        },
    ]
    // Filter menu items based on user permissions
    const filteredMenuItems = useMemo(() => {
        if (!user) return [];

        return menuItemLink.filter(item => {
            // Hide items marked as hidden from superusers
            if (item.hideFromSuperuser && user.is_superuser) {
                return false;
            }

            // Superusers see everything except self-service items
            if (user.is_superuser) {
                if (item.selfService) return false;
                return true;
            }

            // Always hide if user has any excluded permission
            if (item.excludePermissions && hasAnyPermission(item.excludePermissions)) {
                return false;
            }

            // Payroll Approvals is assignee-based now — being picked as reviewer/approver
            // on a run is itself the authorization, so show it even without pay_approve.
            if (item.link === '/approvals/payroll' && hasAssignedPayrollRuns) {
                return true;
            }

            // If no permissions required, show to everyone
            if (!item.permissions || item.permissions.length === 0) {
                return true;
            }

            return hasAnyPermission(item.permissions);
        }).map(item => {
            if (item.children) {
                return {
                    ...item,
                    children: item.children.filter(child => {
                        if (!child.permissions || child.permissions.length === 0) return true;
                        if (user.is_superuser) return true;
                        return hasAnyPermission(child.permissions);
                    })
                };
            }
            return item;
        });
    }, [user, hasAnyPermission, hasAssignedPayrollRuns]);

    // Check if there are any approval items to show (hide tab for superusers)
    const hasApprovalItems = useMemo(() => {
        return filteredMenuItems.some(item => item.projectMenu);
    }, [filteredMenuItems]);

    const orgId = user?.orgId || localStorage.getItem('orgId');

    // Single endpoint for all pending approval counts
    const { data: pendingApprovalsData } = usePendingApprovals(hasApprovalItems ? orgId : null);
    const breakdown = pendingApprovalsData?.breakdown || {};

    // Separate from pending-approvals above (identity-based, not permission-scoped).
    // Gated so users with no veh_* grant don't fire a guaranteed 403.
    const hasVehicleAccess = hasAnyPermission(['rbacapp.veh_view', 'rbacapp.veh_act', 'rbacapp.veh_view_all', 'rbacapp.veh_dispatch']);
    const { data: vehicleSummary } = useVehicleRequestSummary(orgId, { enabled: !!user && hasVehicleAccess });
    const pendingVehicleCount = vehicleSummary?.awaiting_my_action || 0;

    const pendingLeaveCount = (breakdown.leave_requests_as_manager || 0) + (breakdown.leave_requests_as_hr || 0);
    const pendingTimesheetCount = (breakdown.timesheets_as_manager || 0) + (breakdown.timesheets_as_hr || 0);
    const pendingKPICount = breakdown.kpi_sections_as_manager || 0;
    const pendingTravelCount = (breakdown.travel_requests_as_manager || 0) + (breakdown.travel_budgets_as_manager || 0) + (breakdown.travel_requests_as_security || 0) + (breakdown.travel_budgets_as_finance || 0) + (breakdown.travel_advances_as_finance || 0) + (breakdown.travel_prvs_as_finance || 0);

    const totalPendingCount = pendingApprovalsData?.total_pending || 0;

    const pendingFeedbackCount = breakdown.feedback_received || 0;
    // Assignee-based, not the old permission-scoped aggregate — see hasAssignedPayrollRuns above.
    const pendingPayrollCount = myActionablePayrollRuns.length;

    // Map sidebar link → pending count for badge display
    const approvalCounts = {
        '/approvals/leave': pendingLeaveCount,
        '/approvals/timesheet': pendingTimesheetCount,
        '/approvals/kpi': pendingKPICount,
        '/approvals/travel': pendingTravelCount,
        '/approvals/vehicle': pendingVehicleCount,
        '/performance/360-feedback/hr': pendingFeedbackCount,
        '/approvals/payroll': pendingPayrollCount,
    };
    const skinColors = [
        {
            name: "purple",
            theme: "purple",
            color: "bg-theme-purple",
        },
        {
            name: "blue",
            theme: "blue",
            color: "bg-theme-blue",
        },
        {
            name: "cyan",
            theme: "default",
            color: "bg-theme-cyan",
        },
        {
            name: "green",
            theme: "green",
            color: "bg-theme-green",
        },
        {
            name: "orange",
            theme: "orange",
            color: "bg-theme-orange",
        },
        {
            name: "blush",
            theme: "blush",
            color: "bg-theme-blush",
        },
    ]

    const generalSetting = [
        {
            name: "Report Panel Usag",
        },
        {
            name: "Email Redirect",
            checked: true,
        },
        {
            name: "Notifications",
            checked: true,
        },
        {
            name: "Auto Updates",
        },
        {
            name: "Offline",
        },
        {
            name: "Location Permission",
        },
    ]

    return (
        <>
            <div className={`bg-transparent ${miniSidebar ? isHovered ? 'p-20' : 'p-15' : 'p-20'}`}>
                <div className={`flex items-center gap-10 ${miniSidebar ? isHovered ? 'border-b border-b-border pb-20 mb-20' : 'border-b border-b-border pb-15 justify-center' : 'border-b border-b-border pb-20 mb-20'}`}>
                    {/* Initials Avatar */}
                    <div className={`flex-shrink-0 rounded-lg bg-primary flex items-center justify-center text-white font-bold ${miniSidebar ? isHovered ? 'w-[50px] h-[50px] text-base' : 'w-[40px] h-[40px] text-sm' : 'w-[50px] h-[50px] text-base'}`}>
                        {user?.first_name?.[0]?.toUpperCase()}{user?.last_name?.[0]?.toUpperCase()}
                    </div>
                    <div className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : ''}`}>
                        <div className='text-text-secondary text-sm'>
                            Welcome,
                        </div>
                        <div className='flex group relative'>
                            <button className='flex items-center gap-5 font-bold'>
                                <div className='flex flex-col items-start'>
                                    <span>{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username || 'User'}</span>
                                    <span className='text-xs text-text-secondary font-normal capitalize'>
                                        {user?.role?.replace('_', ' ')}
                                    </span>
                                </div>
                                <IconCaretDownFilled className='w-[12px] h-[12px] stroke-[1]' />
                            </button>
                            <div className='absolute left-0 z-[1] top-[50%] w-max border border-border bg-bg-dark shadow-light rounded-lg p-15 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all group-hover:top-full'>
                                <div className='flex flex-col gap-10 pb-10 mb-10 border-b border-border'>
                                    <Link to="/page-profile" className='flex items-center gap-10' onClick={() => { if (window.innerWidth < 1200) setActive(false); }}>
                                        <IconUser className='w-[18px] h-[18px]' />
                                        <span>Basic</span>
                                    </Link>
                                    {/* <button type="button" className='flex items-center gap-10 text-left w-full'>
                                        <IconMailOpened className='w-[18px] h-[18px]' />
                                        <span>Preferences</span>
                                    </button>
                                    <button type="button" className='flex items-center gap-10 text-left w-full'>
                                        <IconSettings className='w-[18px] h-[18px]' />
                                        <span>Privacy</span>
                                    </button> */}
                                </div>
                                <div>
                                    <button
                                        type="button"
                                        onClick={handleLogout}
                                        className='flex items-center gap-10 text-left w-full'
                                    >
                                        <IconPower className='w-[18px] h-[18px]' />
                                        <span>Logout</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Tabs>
                <TabList className={`border-b border-b-border ${miniSidebar ? isHovered ? 'flex' : 'hidden' : 'flex'}`}>
                    <Tab className="sidebar-tab font-medium border border-transparent border-b-0 relative bottom-[-1px] px-[16px] py-[8px] cursor-pointer text-text-primary flex items-center justify-center transition duration-300 hover:text-primary focus:outline-none">
                        HR
                    </Tab>
                    {hasApprovalItems && (
                        <Tab className="sidebar-tab font-medium border border-transparent border-b-0 relative bottom-[-1px] px-[16px] py-[8px] cursor-pointer text-text-primary flex items-center gap-6 justify-center transition duration-300 hover:text-primary focus:outline-none">
                            Approvals
                            {totalPendingCount > 0 && (
                                <span className='inline-flex items-center justify-center min-w-[18px] h-[18px] px-[5px] rounded-full bg-danger text-white text-[10px] font-bold leading-none'>
                                    {totalPendingCount > 99 ? '99+' : totalPendingCount}
                                </span>
                            )}
                        </Tab>
                    )}
                    <Tab title='setting' className="sidebar-tab border border-transparent border-b-0 relative bottom-[-1px] px-[16px] py-[8px] cursor-pointer flex items-center justify-center focus:outline-none">
                        <IconSettings className="stroke-[1.5] text-text-primary w-[18px] h-[18px] transition duration-300 hover:text-primary" />
                    </Tab>
                </TabList>
                <TabPanel>
                    <ul className={`${miniSidebar ? isHovered ? 'py-15' : 'pb-15' : 'py-15'}`}>
                        {filteredMenuItems.map((item, key) => (
                            item.hrMenu && <li key={key} className='pb-5 last:pb-0'>
                                {item.link ?
                                    <Link
                                        to={item.link}
                                        className={`menu-item flex items-center ${miniSidebar ? isHovered ? 'justify-between' : 'justify-center' : 'justify-between'} gap-5 px-15 py-[13px] transition-all duration-300 ease-in-out xl:hover:bg-bg-light-100 ${urlPath === item.link ? 'relative bg-bg-light-100' : ''}`}
                                        onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                    >
                                        <span className='menu-icon flex items-center gap-20 text-[16px]/[20px] text-text-primary'>
                                            <item.icon className='min-w-[22px] min-h-[22px] w-[22px] h-[22px] stroke-[1.5] text-primary' />
                                            <span className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                {item.url}
                                            </span>
                                        </span>
                                    </Link>
                                    :
                                    <>
                                        <button
                                            aria-label='menu item'
                                            onClick={() => menuItemButton(key)}
                                            className={`w-full menu-item flex items-center ${miniSidebar ? isHovered ? 'justify-between' : 'justify-center' : 'justify-between'} gap-5 px-15 py-[13px] transition-all duration-300 ease-in-out xl:hover:bg-bg-light-100 ${menuItem === key ? 'relative bg-bg-light-100' : ''}`}
                                        >
                                            <span className='menu-icon flex items-center gap-20 text-[16px]/[20px] text-text-primary'>
                                                <item.icon className='min-w-[22px] min-h-[22px] w-[22px] h-[22px] stroke-[1.5] text-primary' />
                                                <span className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                    {item.url}
                                                </span>
                                            </span>
                                            <span className={`transition-all duration-300 ease-in-out ${menuItem === key ? '-rotate-90' : ''} ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                <IconChevronLeft className='stroke-[1] text-text-secondary' />
                                            </span>
                                        </button>
                                        {menuItem === key && <ul
                                            className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                            {item.children.filter(res => {
                                                if (res.excludePermissions && hasAnyPermission(res.excludePermissions)) return false;
                                                if (res.permissions && !hasAnyPermission(res.permissions)) return false;
                                                return true;
                                            }).map((res, key) => (
                                                <li key={key}>
                                                    {res.link ? <Link
                                                        to={res.link}
                                                        className={`pl-[57px] pr-15 py-10 block relative text-text-secondary h-40 before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === res.link ? 'font-bold' : ''}`}
                                                        onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                                    >
                                                        <span>
                                                            {res.url}
                                                        </span>
                                                    </Link>
                                                        :
                                                        <>
                                                            <button
                                                                aria-label='menu item'
                                                                onClick={() => subMenuItemButton(key)}
                                                                className={`pl-[57px] pr-15 py-10 flex items-center justify-between gap-5 w-full relative text-text-secondary before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === res.link ? 'font-bold' : ''}`}
                                                            >
                                                                <span className={`text-[16px]/[20px] text-text-primary`}>
                                                                    {res.url}
                                                                </span>
                                                                <span className={`transition-all duration-300 ease-in-out ${subMenuItem === key ? '-rotate-90' : ''}`}>
                                                                    <IconChevronLeft className='stroke-[1] text-text-secondary' />
                                                                </span>
                                                            </button>
                                                            {subMenuItem === key && <ul>
                                                                {res.children.map((sub, key) => (
                                                                    <li key={key}>
                                                                        <Link
                                                                            to={sub.link}
                                                                            className={`pl-[57px] pr-15 py-10 block relative text-text-secondary h-40 before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === sub.link ? 'font-bold' : ''}`}
                                                                            onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                                                        >
                                                                            <span>
                                                                                {sub.url}
                                                                            </span>
                                                                        </Link>
                                                                    </li>))}
                                                            </ul>}
                                                        </>
                                                    }
                                                </li>
                                            ))}
                                        </ul>}
                                    </>
                                }
                            </li>
                        ))}
                    </ul>
                </TabPanel>
                {hasApprovalItems && (
                    <TabPanel>
                        <ul className={`${miniSidebar ? isHovered ? 'py-15' : 'pb-15' : 'py-15'}`}>
                            {filteredMenuItems.map((item, key) => (
                                item.projectMenu && <li key={key} className='pb-5 last:pb-0'>
                                    {item.link ?
                                        <Link
                                            to={item.link}
                                            className={`menu-item flex items-center ${miniSidebar ? isHovered ? 'justify-between' : 'justify-center' : 'justify-between'} gap-5 px-15 py-[13px] transition-all duration-300 ease-in-out xl:hover:bg-bg-light-100 ${urlPath === item.link ? 'relative bg-bg-light-100' : ''}`}
                                            onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                        >
                                            <span className='menu-icon flex items-center gap-20 text-[16px]/[20px] text-text-primary'>
                                                <item.icon className='min-w-[22px] min-h-[22px] w-[22px] h-[22px] stroke-[1.5] text-primary' />
                                                <span className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                    {item.url}
                                                </span>
                                            </span>
                                            {approvalCounts[item.link] > 0 && (
                                                <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-[5px] rounded-full bg-danger text-white text-[10px] font-bold leading-none ${miniSidebar ? isHovered ? 'flex' : 'hidden' : 'flex'}`}>
                                                    {approvalCounts[item.link] > 99 ? '99+' : approvalCounts[item.link]}
                                                </span>
                                            )}
                                        </Link>
                                        :
                                        <>
                                            <button
                                                aria-label='menu item'
                                                onClick={() => menuItemButton(key)}
                                                className={`w-full menu-item flex items-center ${miniSidebar ? isHovered ? 'justify-between' : 'justify-center' : 'justify-between'} gap-5 px-15 py-[13px] transition-all duration-300 ease-in-out xl:hover:bg-bg-light-100 ${menuItem === key ? 'relative bg-bg-light-100' : ''}`}
                                            >
                                                <span className='menu-icon flex items-center gap-20 text-[16px]/[20px] text-text-primary'>
                                                    <item.icon className='min-w-[22px] min-h-[22px] w-[22px] h-[22px] stroke-[1.5] text-primary' />
                                                    <span className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                        {item.url}
                                                    </span>
                                                </span>
                                                <span className={`transition-all duration-300 ease-in-out ${menuItem === key ? '-rotate-90' : ''} ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                    <IconChevronLeft className='stroke-[1] text-text-secondary' />
                                                </span>
                                            </button>
                                            {menuItem === key && <ul
                                                className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                                {item.children.filter(res => {
                                                    if (res.excludePermissions && hasAnyPermission(res.excludePermissions)) return false;
                                                    if (res.permissions && !hasAnyPermission(res.permissions)) return false;
                                                    return true;
                                                }).map((res, key) => (
                                                    <li key={key}>
                                                        {res.link ? <Link
                                                            to={res.link}
                                                            className={`pl-[57px] pr-15 py-10 block relative text-text-secondary h-40 before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === res.link ? 'font-bold' : ''}`}
                                                            onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                                        >
                                                            <span>
                                                                {res.url}
                                                            </span>
                                                        </Link>
                                                            :
                                                            <>
                                                                <button
                                                                    aria-label='menu item'
                                                                    onClick={() => subMenuItemButton(key)}
                                                                    className={`pl-[57px] pr-15 py-10 flex items-center justify-between gap-5 w-full relative text-text-secondary before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === res.link ? 'font-bold' : ''}`}
                                                                >
                                                                    <span className={`text-[16px]/[20px] text-text-primary`}>
                                                                        {res.url}
                                                                    </span>
                                                                    <span className={`transition-all duration-300 ease-in-out ${subMenuItem === key ? '-rotate-90' : ''}`}>
                                                                        <IconChevronLeft className='stroke-[1] text-text-secondary' />
                                                                    </span>
                                                                </button>
                                                                {subMenuItem === key && <ul>
                                                                    {res.children.map((sub, key) => (
                                                                        <li key={key}>
                                                                            <Link
                                                                                to={sub.link}
                                                                                className={`pl-[57px] pr-15 py-10 block relative text-text-secondary h-40 before:content-["--"] before:absolute before:left-20 before:top-[50%] before:translate-y-[-50%] transition-all duration-300 ease-in-out hover:bg-bg-light-100 ${urlPath === sub.link ? 'font-bold' : ''}`}
                                                                                onClick={() => { window.innerWidth < 1200 && setActive(false) }}
                                                                            >
                                                                                <span>
                                                                                    {sub.url}
                                                                                </span>
                                                                            </Link>
                                                                        </li>))}
                                                                </ul>}
                                                            </>
                                                        }
                                                    </li>
                                                ))}
                                            </ul>}
                                        </>
                                    }
                                </li>
                            ))}
                        </ul>
                    </TabPanel>
                )}
                <TabPanel>
                    <div className={`${miniSidebar ? isHovered ? 'p-15' : 'px-15 pb-15' : 'p-15'}`}>
                        <p className={`text-[16px]/[19.2px] font-medium mb-2 ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                            Theme Option
                        </p>
                        <div className={`flex items-center gap-10 mb-10 ${miniSidebar ? isHovered ? '' : 'justify-center' : ''}`}>
                            <label className='cus-switch'>
                                <input type="checkbox" defaultChecked={darkmode} />
                                <span
                                    className="checkmark"
                                    onClick={toggle_darkmode}
                                ></span>
                            </label>
                            <p className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>Enable Dark Mode!</p>
                        </div>
                        <div className={`xl:flex hidden items-center gap-10 mb-10 ${miniSidebar ? isHovered ? '' : 'justify-center' : ''}`}>
                            <label className='cus-switch'>
                                <input type="checkbox" defaultChecked={miniSidebar} />
                                <span className="checkmark" onClick={toggle_miniSidebar} ></span>
                            </label>
                            <p className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>Mini Sidebar</p>
                        </div>
                        <hr className='my-15 border-border' />
                        <p className={`text-[16px]/[19.2px] font-medium mb-2 ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                            Choose Skin
                        </p>
                        <ul className={`flex flex-col gap-10 ${miniSidebar ? isHovered ? '' : 'items-center justify-center' : ''}`}>
                            {skinColors.map((item, key) => (
                                <li key={key}>
                                    <button
                                        className='flex items-center gap-10 w-full'
                                        onClick={() => handleOptionClick(item.theme)}
                                    >
                                        <span className={`inline-block w-[22px] h-[22px] min-w-[22px] min-h-[22px] rounded-full ${item.color} ${selectedOption === item.theme ? "bg-[url('../images/check-solid.svg')] bg-no-repeat bg-center bg-[length:10px_10px]" : ""}`}></span>
                                        <p className={`text-text-primary capitalize ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                            {item.name}
                                        </p>
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {/* <hr className='my-15 border-border' /> */}
                        {/* <p className={`text-[16px]/[19.2px] font-medium mb-2 ${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                            General Settings
                        </p> */}
                        {/* <ul className={`flex flex-col gap-10 ${miniSidebar ? isHovered ? '' : 'items-center justify-center' : ''}`}>
                            {generalSetting.map((item, key) => (
                                <li key={key}>
                                    <label className='cus-checkbox text-text-primary flex items-center gap-10'>
                                        <input type='checkbox' defaultChecked={item.checked} />
                                        <span className='checkmark'></span>
                                        <span className={`${miniSidebar ? isHovered ? 'block' : 'hidden' : 'block'}`}>
                                            {item.name}
                                        </span>
                                    </label>
                                </li>
                            ))}
                        </ul> */}
                    </div>
                </TabPanel>
            </Tabs>

        </>
    )
}
